import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, jsonBody, setCsrfToken } from './api';
import type { UserView } from './types';

interface AuthContextValue {
  user: UserView | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthPayload {
  user: UserView;
  csrf_token: string;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserView | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const result = await api<AuthPayload>('/v1/auth/me');
      setCsrfToken(result.csrf_token);
      setUser(result.user);
    } catch {
      setCsrfToken('');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      refresh,
      login: async (username, password) => {
        const result = await api<AuthPayload>('/v1/auth/login', {
          method: 'POST',
          ...jsonBody({ username, password }),
        });
        setCsrfToken(result.csrf_token);
        setUser(result.user);
      },
      logout: async () => {
        await api('/v1/auth/logout', { method: 'POST' });
        setCsrfToken('');
        setUser(null);
      },
    }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('AuthProvider 未初始化');
  return context;
}
